/**
 * The "opened in another tab" card (fix I) — what the old `ConnectionModal`
 * showed as `visible={changeClient || !authComplete}`
 * (`Screens/PlayChess/index.js` L5777-5782). This MODEL still computes its
 * own two faces independently of the old component's internals — only the
 * VIEW changed: fix O (2026-10-06, owner's call, "keep the original design
 * for now") made `View.tsx` reuse the old `ConnectionModal` BY IMPORT to
 * draw them, so (unlike when this sentence was first written) the old
 * component IS now imported from this tree — see `View.tsx`.
 *
 * It is rendered at the SCREEN ROOT (`index.tsx`, a sibling after
 * `<PlayChessV2Body/>`) so it covers the lobby AND the game, and reads
 * `Strip/authStatus` rather than GamePlayStore or a prop directly — only the
 * root may read either, and it already mirrors both into that store
 * (`clientChanged`, `complete`, `clearRemoteClientChanged`) for exactly this
 * reason (fix I1).
 *
 * `anotherTab` outranks `authFailed` BY CONSTRUCTION, not by an extra
 * ordering rule: `authFailed`'s own formula excludes `clientChanged`, so the
 * two can never both be true (the same precedence `Strip/model.ts` used for
 * its own, now-removed, two faces — fix I3). `authFailed` ALSO needs
 * `connected` (not on `authStatus`): `SocketProvider` lowers `complete` on
 * every disconnect too, and a stranded tab mid an ordinary network blip must
 * not see "Auth Failed" for a reconnect that is already under way — the
 * Strip's own 'reconnecting' fallback still owns that window.
 *
 * Fix I REVIEW (2026-10-06) ADDS a grace window on top of that raw formula:
 * `SocketProvider.reconnect()` lowers `complete` BEFORE the transport even
 * drops, and `connected` flips back to `true` the instant the socket
 * re-opens — a whole round trip before the next `auth_complete` lands. Read
 * literally, `authFailedRaw` is briefly true on EVERY ordinary reconnect, not
 * only a genuine refusal. `authFailed` (what the View actually reads) only
 * follows once `authFailedRaw` has HELD for `AUTH_FAIL_GRACE_MS`, via one
 * `setTimeout` cleared whenever the raw condition drops — a genuine refusal
 * still shows well within the window; an ordinary reconnect's round trip
 * does not.
 */
import {useCallback, useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {bridgeSetShowChangeInClient} from '@/Store/liveGame/gamePlayStoreWrites';
import {useLiveGame} from '@/Store/liveGameStore';

import {requestReconnect} from '../../rail/navigateRef';
import {useSocketAuth} from '../../Strip/authStatus';

/** comfortably past an ordinary `auth_complete` round trip; still prompt for
 * a genuine refusal */
export const AUTH_FAIL_GRACE_MS = 2500;

export type ClientChangedModel = {
  /** the game is open in another tab — outranks a refused token */
  anotherTab: boolean;
  /** the socket is up but the server refused the token (E6), past the grace window */
  authFailed: boolean;
  visible: boolean;
  onContinue: () => void;
};

export function useClientChangedModel(): ClientChangedModel {
  const connected = useLiveGame(s => s.session.connected);
  const {anotherTab, authFailedRaw} = useSocketAuth(
    useShallow(s => ({
      anotherTab: s.clientChanged,
      authFailedRaw: connected && !s.complete && !s.clientChanged,
    })),
  );

  const [authFailed, setAuthFailed] = useState(false);
  useEffect(() => {
    if (!authFailedRaw) {
      setAuthFailed(false);
      return undefined;
    }
    const timer = setTimeout(() => setAuthFailed(true), AUTH_FAIL_GRACE_MS);
    return () => clearTimeout(timer);
  }, [authFailedRaw]);

  /**
   * The old `handleContinue` exactly (`PlayChess/index.js` L4237-4239): lower
   * the GamePlayStore source, lower the REMOTE `changeClient` prop
   * (`clearRemoteClientChanged`, fix I1's bridge into `props.setChangeClient`)
   * and reconnect — the same `requestReconnect` the Strip's own action uses.
   */
  const onContinue = useCallback(() => {
    bridgeSetShowChangeInClient(false);
    useSocketAuth.getState().clearRemoteClientChanged();
    requestReconnect();
  }, []);

  return {
    anotherTab,
    authFailed,
    visible: anotherTab || authFailed,
    onContinue,
  };
}
